import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useDeleteConfirmation } from '@client/hooks/ui/useDeleteConfirmation';
import type { RouterInputs } from '@client/trpc/client';
import { DEFAULT_MAX_ACCESSIBLE_RECORDS, DEFAULT_PAGE_SIZE, SYSTEM_ACCOUNT_IDS } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { UsersRound } from 'lucide-react';
import { useState } from 'react';
import { AdminUserFilters } from './AdminUserFilters';
import { useAdminUserColumns } from './columns';

export const Route = createFileRoute('/admin-console/user-access/users')({
    staticData: {
        permissions: ['admin_users:read'],
        breadcrumb: {
            title: 'Users',
        },
        ui: {
            icon: UsersRound,
            title: 'Users',
            subtitle: 'Manage your users',
            widthVariant: 'fullWidth',
            menuDescription: 'Manage platform users',
        },
    },
    component: AdminUsers,
});

type QueryVariables = RouterInputs['admin']['user']['listUsers'];

const DEFAULT_FILTER = {
    AND: [{ id: { gt: SYSTEM_ACCOUNT_IDS.SYSTEM } }],
} satisfies QueryVariables['where'];

function AdminUsers() {
    const { trpcClient, trpc, trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const user = useUser();
    useAdminModulePermissions('users');

    const [filterWhere, setFilterWhere] = useState<QueryVariables['where']>({});

    // Use usePathState to persist table filtering, sorting, and pagination settings
    const [queryVariables, setQueryVariables] = usePathState<RouterInputs['admin']['user']['listUsers']>(
        'queryVariables',
        {
            orderBy: [{ createdAt: 'desc' }],
            limit: DEFAULT_PAGE_SIZE,
        } as RouterInputs['admin']['user']['listUsers'],
    );

    // Merge default filter with user-selected filters
    const mergedWhere = {
        AND: [...DEFAULT_FILTER.AND, ...(filterWhere ? [filterWhere] : [])],
    };

    const { mutate: deleteUser } = trpc.admin.user.deleteUser.useMutation();

    const { showConfirmationModal, contextHolder: DeleteConfirmationModal } = useDeleteConfirmation<{
        userId: bigint;
    }>({
        entityDisplayName: 'user',
        onConfirm: async (ctx) => {
            message.loading('Deleting user...');
            deleteUser(
                {
                    userId: ctx.params.userId,
                    email: ctx.confirmationText,
                },
                {
                    onSuccess: async () => {
                        message.success('User deleted successfully');
                        await trpcUtils.admin.user.listUsers.invalidate();
                    },
                    onError: (error) => {
                        errorMessage.show(error);
                    },
                },
            );
        },
    });

    const { data, refetch, isFetching, error } = trpc.admin.user.listUsers.useQuery({
        ...queryVariables,
        where: mergedWhere,
    });

    const onSync = async (userId: bigint) => {
        try {
            message.loading('Syncing profile...');
            const result = await trpcClient.admin.user.syncUserProfileToAuth0.mutate({
                userId: userId!,
            });
            if (result.success) {
                message.success('Profile synced to Auth0.');
            } else {
                errorMessage.show(result.error, 'Profile sync failed');
            }
        } catch (e) {
            errorMessage.show(e);
        }
    };

    const columns = useAdminUserColumns(navigate, onSync, showConfirmationModal, user);

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isFetching,
        columns,
        rowKey: 'id',
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: {
            position: 'both',
            showSizeChanger: true,
            maxAccessibleRecords: DEFAULT_MAX_ACCESSIBLE_RECORDS,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'admin-users-table-columns',
            enableReordering: true,
            defaultHidden: ['photoUrl', 'auth0Id', 'oid', 'syncWithAuth0InDev', 'lastSynced'],
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
    });

    return (
        <PageController route={Route}>
            <ReadOnlyBanner module="users" />
            <TRPCErrorBoundary error={error}>
                {DeleteConfirmationModal}

                <TableToolbar filters={<AdminUserFilters onChange={setFilterWhere} />} variant="enhanced" />

                <PrismaTable {...tableProps} />
            </TRPCErrorBoundary>
            <Outlet />
        </PageController>
    );
}
